import { letterFormText } from "@talent/tasks";
import type { CardPainter } from "./choice-task.js";
import { fitGlyph, letterGlyph } from "./glyph.js";

/**
 * A card with one Persian letter on it.
 *
 * The whole of the letters game's rendering. Everything else it needs — the layout that survives
 * a phone's address bar sliding away, the columns derived from the touch floor, the padded hit
 * areas, the right/wrong feedback and the trial loop — already existed in `choice-task.ts` and is
 * shared rather than copied. What was actually new was this: a card with a letter instead of a
 * card with dots.
 *
 * The content is `[letterIndex, positionIndex]`, straight out of the generator and straight into
 * the event record. This file is the only place those two integers become something visible, and
 * it does not know what letter it is drawing — `letterFormText` does, and it lives with the
 * alphabet in the content bundle where a stimulus belongs.
 */

/**
 * Half the card, near enough.
 *
 * Big for a reason: on half the levels the entire difference between the right card and a wrong
 * one is where two or three dots sit. A letter drawn small would make that a test of eyesight,
 * and the same fraction is what keeps the target above and the cards below at one apparent size
 * on any screen.
 *
 * (No example letters in this comment: `check-copy.ts` refuses Persian script anywhere outside
 * `copy.ts`, comments included, and it is right to.)
 */
const RELATIVE_SIZE = 0.52;

/** How much of the card a letter's ink may take, in both directions. */
const FIT_ROOM = 0.72;

export const paintLetter: CardPainter = (g, content, size, _state, ink) => {
  const [letterIndex, positionIndex] = content;
  if (letterIndex === undefined || positionIndex === undefined) return;

  const form = letterFormText(letterIndex, positionIndex);
  const fontSize = size * RELATIVE_SIZE;
  const glyph = letterGlyph(form, {
    size: fontSize,
    color: ink,
    // Heaviest of the three weights. A letter on a dark panel needs it to hold its counters, and
    // the dots — which are the entire question on half the levels — need it most of all.
    weight: "800",
  });

  /**
   * Fitted and centred on the ink, not on the font's box.
   *
   * The forms are nowhere near the same size: a letter with a connecting stroke on both sides is
   * several times wider than the same letter alone, and the ones carrying marks below the line are
   * much taller than the ones that do not. A constant tuned against one letter is wrong for another,
   * and the failure was visible on a phone before it was visible here — the dots ran off the card.
   */
  fitGlyph(glyph, glyph.text, fontSize, size * FIT_ROOM);

  g.addChild(glyph);
};
